import { Component, OnInit } from '@angular/core';
import { CommonStateService } from 'src/state/common/service';
import { CalsiumListColumnItem, CalsiumListConfig, CalsiumListSelectItem, ColumnDataType } from 'src/app/calsiumlist/calsiumlist.component.model';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { CalsiumDateFormatter } from 'src/app/shared/calsium-date-formatter';
import { Observable, Subscription, combineLatest, firstValueFrom, from, of } from 'rxjs';
import { Market, Material, Paging } from 'src/state';
import { debounceTime, map, switchMap } from 'rxjs/operators';
import { MissingSIMMaterial } from 'src/state/missing-sim-material/state-models/missing-sim-material';
import { MissingSIMMaterialListStateService } from 'src/state/missing-sim-material/service';
import { MaterialSearchForm } from 'src/state/missing-sim-material/state-models/missing-material-search-form';
import { ErrorHandlerService } from 'src/app/errorhandler/error-handler.service';
import { ErrorTypeEnum } from 'src/app/shared/enums.model';
import { MarketApiService } from 'src/api/services/market-api.service';

@Component({
    selector: 'cal-missing-sim-materials',
    templateUrl: './missing-sim-materials.component.html',
    styleUrls: ['./missing-sim-materials.component.css'],
    standalone: false
})
export class MissingSIMMaterialsComponent implements OnInit {

  resources: any = this.commonService.getResources();
  searchComponentsForm: UntypedFormGroup;
  materialSearchForm: Observable<MaterialSearchForm>
  listConfig: CalsiumListConfig;

  dataList: Observable<MissingSIMMaterial[]>;
  paging: Observable<Paging>;
  totalCount: Observable<number>;
  errorCount: Observable<number>;
  subscriptions = new Subscription();
  isLoading: Observable<boolean>;
  sortingExpression: Observable<string>;
  filterExpression: Observable<string>;
  marketList: Observable<Market[]>;
  selectedMarketId: number = 0;
  endDateMin: string;

  constructor(
    private commonService: CommonStateService,
    private missingSIMMaterialService: MissingSIMMaterialListStateService,
    private errHandler: ErrorHandlerService,
    private formBuilder: UntypedFormBuilder,
    private marketApi: MarketApiService
  ) { }

  ngOnInit() {
    this.isLoading = this.commonService.getLoading();
    this.sortingExpression = this.missingSIMMaterialService.getSortingExpression();
    this.filterExpression = this.missingSIMMaterialService.getFilterExpression();
    this.totalCount = this.missingSIMMaterialService.getTotalCount();
    this.paging = this.missingSIMMaterialService.getPaging();
    this.dataList = this.missingSIMMaterialService.getList();
    this.materialSearchForm = this.missingSIMMaterialService.getMaterialSearchForm();

    let pageInitialLoad = true;
    this.subscriptions.add(
      combineLatest(this.materialSearchForm, this.paging, this.sortingExpression, this.filterExpression).pipe(
        debounceTime(0),
        switchMap(([searchForm, paging, sortingExpression, filterExpression]) => {
          if (searchForm) {
            this.fillSearchForm(searchForm.fromDate, searchForm.toDate, searchForm.marketId);
          }

          if (!pageInitialLoad) {
            return this.missingSIMMaterialService.loadMissingSIMMaterialList(searchForm,
              paging,
              sortingExpression,
              filterExpression,
              true
            );
          }
          pageInitialLoad = false;
          return new Promise<void>(null);
        })
      ).subscribe()
    );
    this.fillSearchForm(null, null, null);
    this.marketList = from(this.marketApi.getMarket());
    this.buildList();

  }

  ngOnDestroy() { this.subscriptions.unsubscribe(); }

  fillSearchForm(fromDate: Date = null, toDate: Date = null, market: number ) {
    this.searchComponentsForm = this.formBuilder.group({
      startDate: [CalsiumDateFormatter.getDateOrDefault(fromDate, true), []],
      endDate: [CalsiumDateFormatter.getDateOrDefault(toDate, false), []],
      selectedMarket: [market, <any>Validators.required]
    });
    this.endDateMin = CalsiumDateFormatter.getDateOrDefault(fromDate, true);
    this.searchComponentsForm.get('startDate')?.valueChanges
      .subscribe((newStartDateString: string) => {
        this.endDateMin = newStartDateString;
      });
  }
  buildList() {
    this.listConfig = new CalsiumListConfig();
    this.listConfig.ListCaption = this.resources.List;
    this.listConfig.ShowFilterRow = true;
    this.listConfig.ParentComponent = this;
    this.listConfig.ShowExportToExcel = true;
    let column;
    column = this.listConfig.AddListColumn(ColumnDataType.number, 'materialNumber', this.resources.MaterialNumber);
    column.AllowFilter = true;
    column.Sortable = true;

    column = this.listConfig.AddListColumn(ColumnDataType.string, 'materialName', this.resources.MaterialName);
    column.AllowFilter = true;
    column.Sortable = true;

    column = this.listConfig.AddListColumn(ColumnDataType.string, 'componentType', this.resources.ComponentType);
    column.AllowFilter = true;
    column.Sortable = true;

    column = this.listConfig.AddListColumn(ColumnDataType.string, 'purchaserCode', this.resources.PurchaserCode);
    column.AllowFilter = true;
    column.Sortable = true;

    column = this.listConfig.AddListColumn(ColumnDataType.list, 'marketId', this.resources.Market);
    column.DisplayColumnName = 'market.name';
    column.AllowFilter = false;
    column.Sortable = false;
    column.FilterOptions.FilterListItems = this.marketList.pipe(
     map(markets =>
       markets.map(market =>
         CalsiumListSelectItem.getInstance(
           market.id.toString(),
           market.name
         )
       )
     )
    );
column.FilterOptions.AddEmptyDropDownListItem = false;

}

  onPaging(page: number) {
    this.missingSIMMaterialService.goToPage(page);
  }

  onSort(sortExpression: string) {
    this.missingSIMMaterialService.sort(sortExpression);
  }

  onFilter(filterExpression: string) {
    this.missingSIMMaterialService.filter(filterExpression);
  }

  onClearFilterAndSorting() {
    this.missingSIMMaterialService.clearFilterAndSorting();
  }

  onSearch() {
    if (this.isValidForm()) {
      if (this.searchComponentsForm.valid) {
        const fromDate = this.searchComponentsForm.value.startDate;
        const toDate = this.searchComponentsForm.value.endDate;
        const marketId = Number(this.searchComponentsForm.value.selectedMarket);
        const param: MaterialSearchForm = {
          fromDate,
          toDate,
          marketId
        };
        this.missingSIMMaterialService.changeMaterialSearchForm(param);
      }
    }
  }


  async onExportToExcel() {
    if (!this.isValidForm()) return;
  
    const fromDate = this.searchComponentsForm.value.startDate;
    const toDate = this.searchComponentsForm.value.endDate;
    const marketId = Number(this.searchComponentsForm.value.selectedMarket);
  
    const markets = await firstValueFrom(this.marketList);
    const marketName = marketId === 0 ? 'All' : (markets.find(x => x.id === marketId)?.name);
  
    const fileName = `MissingSimMaterials_${fromDate}_${toDate}_${marketName}`;
    const payload = {
      from: new Date(fromDate).toISOString(),
      to: new Date(toDate).toISOString(),
      marketId
    };
  
    return this.missingSIMMaterialService.exportMissingMaterialExcel(fileName, payload);
  }
  

  isValidForm(): boolean {
    let valid = true;
    const startDate = this.searchComponentsForm.get('startDate')?.value;
    const endDate = this.searchComponentsForm.get('endDate')?.value;
    const selectedMarket = this.searchComponentsForm.get('selectedMarket')?.value;
    if (selectedMarket === null || selectedMarket === undefined || selectedMarket === '') {
      this.errHandler.emitError(
        this.resources.MarketRequired,
        this.resources.Error,
        ErrorTypeEnum.Error);
      valid = false;
    }

    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      this.errHandler.emitError(
        this.resources.ValidFromValidToCorrect,
        this.resources.Error,
        ErrorTypeEnum.Error);
      valid = false;
    }

    return valid;
  }

}
